import React from 'react';
import { Form1008Data, ValidationErrors } from '../../types';
import { TextInput, NumberInput, CurrencyInput, PercentInput, Select, SegmentedControl, TextArea } from '../ui/Inputs';
import { calculateMetrics } from '../../utils/calculations';
interface Props {
  data: Form1008Data;
  onChange: (field: keyof Form1008Data, value: any) => void;
  errors: ValidationErrors;
}
export function Section3({
  data,
  onChange,
  errors
}: Props) {
  const metrics = calculateMetrics(data);
  const isDSCR = data.qualMethod === 'dscr';
  return <div className="space-y-3" data-id="element-107">
      {/* Callout */}
      <div className="bg-[#0b335e] rounded-lg p-4 flex flex-wrap gap-8 items-center text-white" data-id="element-108">
        {isDSCR ? <div className="flex flex-col" data-id="element-109">
            <span className="text-[10px] font-bold text-slate-300 uppercase tracking-wider" data-id="element-110">
              DSCR (RENT ÷ PITIA)
            </span>
            <div className="flex items-baseline space-x-2" data-id="element-111">
              <span className={`text-2xl font-bold ${metrics.dscr >= 1.1 ? 'text-[#4ade80]' : 'text-amber-400'}`} data-id="element-112">
                {metrics.dscr.toFixed(4)}×
              </span>
            </div>
          </div> : <div className="flex flex-col" data-id="element-113">
            <span className="text-[10px] font-bold text-slate-300 uppercase tracking-wider" data-id="element-114">
              DTI
            </span>
            <span className="text-2xl font-bold text-white" data-id="element-115">
              {metrics.dti.toFixed(2)}%
            </span>
          </div>}
        <div className="w-px h-10 bg-slate-700 hidden md:block" data-id="element-116"></div>
        <div className="flex flex-col" data-id="element-117">
          <span className="text-[10px] font-bold text-slate-300 uppercase tracking-wider" data-id="element-118">
            LTV
          </span>
          <span className="text-xl font-bold text-white" data-id="element-119">
            {metrics.ltv.toFixed(1)}%
          </span>
        </div>
        <div className="w-px h-10 bg-slate-700 hidden md:block" data-id="element-120"></div>
        <div className="flex flex-col" data-id="element-121">
          <span className="text-[10px] font-bold text-slate-300 uppercase tracking-wider" data-id="element-122">
            REP. SCORE
          </span>
          <span className="text-xl font-bold text-white" data-id="element-123">
            {data.repCreditScore || '—'}
          </span>
        </div>
        <div className="w-px h-10 bg-slate-700 hidden md:block" data-id="element-124"></div>
        <div className="flex flex-col" data-id="element-125">
          <span className="text-[10px] font-bold text-slate-300 uppercase tracking-wider" data-id="element-126">
            RESERVES
          </span>
          <span className="text-xl font-bold text-white" data-id="element-127">
            {data.monthsReserves || 0} mo
          </span>
        </div>
        {isDSCR && <div className="ml-4 text-xs text-slate-300 max-w-xs leading-tight" data-id="element-128">
            {metrics.dscr >= 1.1 ? 'DSCR program — qualified on subject cash flow per Form 1007. Coverage meets the 1.10 floor.' : 'DSCR program — qualified on subject cash flow per Form 1007. Below 1.10 floor — review required.'}
          </div>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2" data-id="element-129">
        <SegmentedControl label="Qualification Method" value={data.qualMethod} onChange={v => onChange('qualMethod', v)} helperText="Drives whether DTI or DSCR is used to qualify" options={[{
        label: 'DSCR (Investor)',
        value: 'dscr'
      }, {
        label: 'Full-Doc DTI',
        value: 'fullDoc'
      }]} data-id="element-130" />
        <NumberInput label="Representative Credit / Indicator Score" value={data.repCreditScore} onChange={v => onChange('repCreditScore', v)} required error={errors.repCreditScore} data-id="element-131" />
      </div>

      <div className="pt-3 border-t border-slate-100 grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-2" data-id="element-132">
        <TextInput label="Underwriter's Name" value={data.underwriterName} onChange={v => onChange('underwriterName', v)} required error={errors.underwriterName} data-id="element-133" />
        <TextInput label="Appraiser's Name / License #" value={data.appraiserName} onChange={v => onChange('appraiserName', v)} required error={errors.appraiserName} data-id="element-134" />
        <TextInput label="Appraisal Company Name" value={data.appraisalCompany} onChange={v => onChange('appraisalCompany', v)} data-id="element-135" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-4" data-id="element-136">
        {/* Income Table */}
        <div className="border border-slate-200 rounded-lg overflow-hidden" data-id="element-137">
          <div className="bg-slate-50 px-4 py-2 border-b border-slate-200" data-id="element-138">
            <h4 className="font-semibold text-slate-800 text-[12px]" data-id="element-139">
              Stable Monthly Income
            </h4>
          </div>
          <div className="p-4 space-y-2" data-id="element-140">
            <CurrencyInput label="Borrower 1" value={data.income1} onChange={v => onChange('income1', v)} data-id="element-141" />
            <CurrencyInput label="Borrower 2" value={data.income2} onChange={v => onChange('income2', v)} data-id="element-142" />
            <CurrencyInput label="Borrower 3" value={data.income3} onChange={v => onChange('income3', v)} data-id="element-143" />
            <CurrencyInput label="Borrower 4" value={data.income4} onChange={v => onChange('income4', v)} data-id="element-144" />
            <CurrencyInput label="Other Borrowers (5+)" value={data.incomeOther} onChange={v => onChange('incomeOther', v)} data-id="element-145" />
            <CurrencyInput label="Rental Income — subject property" value={data.rentalIncomeSubject} onChange={v => onChange('rentalIncomeSubject', v)} data-id="element-146" />
            <CurrencyInput label="Net Rental Income — other properties" value={data.netRentalOther} onChange={v => onChange('netRentalOther', v)} data-id="element-147" />
            <div className="pt-3 border-t border-slate-200 flex justify-between items-center" data-id="element-148">
              <span className="font-semibold text-slate-700 text-[12px]" data-id="element-149">
                Total Borrower Income
              </span>
              <span className="font-bold text-slate-900 text-[13px]" data-id="element-150">
                $
                {metrics.totalIncome.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}
              </span>
            </div>
            <div className="pt-2" data-id="element-151">
              <label className="flex items-center space-x-2 text-[12px] text-slate-700 cursor-pointer" data-id="element-152">
                <input type="checkbox" checked={data.selfEmployed} onChange={e => onChange('selfEmployed', e.target.checked)} className="rounded border-slate-300 text-blue-600 focus:ring-blue-500" data-id="element-153" />
                <span data-id="element-154">At least one borrower is self-employed</span>
              </label>
            </div>
          </div>
        </div>

        {/* Payment Table */}
        <div className="border border-slate-200 rounded-lg overflow-hidden" data-id="element-155">
          <div className="bg-slate-50 px-4 py-2 border-b border-slate-200" data-id="element-156">
            <h4 className="font-semibold text-slate-800 text-[12px]" data-id="element-157">
              Proposed Monthly Payment for the Property
            </h4>
          </div>
          <div className="p-4 space-y-2" data-id="element-158">
            <div className="flex justify-between items-center py-1" data-id="element-159">
              <span className="text-[12px] font-medium text-slate-700" data-id="element-160">
                First Mortgage P&I
              </span>
              <span className="text-[12px] font-semibold text-slate-900" data-id="element-161">
                $
                {metrics.firstMortgagePI.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}
              </span>
            </div>
            <CurrencyInput label="Subordinate Lien(s) P&I" value={data.subordinatePI} onChange={v => onChange('subordinatePI', v)} data-id="element-162" />
            <CurrencyInput label="Homeowner's Insurance" value={data.homeownersInsurance} onChange={v => onChange('homeownersInsurance', v)} required error={errors.homeownersInsurance} data-id="element-163" />
            <CurrencyInput label="Supplemental Property Insurance" value={data.supplementalInsurance} onChange={v => onChange('supplementalInsurance', v)} data-id="element-164" />
            <CurrencyInput label="Property Taxes" value={data.propertyTaxes} onChange={v => onChange('propertyTaxes', v)} required error={errors.propertyTaxes} data-id="element-165" />
            <CurrencyInput label="Mortgage Insurance" value={data.mortgageInsurance} onChange={v => onChange('mortgageInsurance', v)} data-id="element-166" />
            <CurrencyInput label="Association / Project Dues" value={data.hoaDues} onChange={v => onChange('hoaDues', v)} data-id="element-167" />
            <CurrencyInput label="Other" value={data.otherHousing} onChange={v => onChange('otherHousing', v)} data-id="element-168" />
            <div className="pt-3 border-t border-slate-200 flex justify-between items-center" data-id="element-169">
              <span className="font-semibold text-slate-700 text-[12px]" data-id="element-170">
                Total Housing Payment
              </span>
              <span className="font-bold text-slate-900 text-[13px]" data-id="element-171">
                $
                {metrics.totalHousing.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}
              </span>
            </div>
            <div className="pt-4 mt-2 border-t border-slate-200" data-id="element-172">
              <CurrencyInput label="All Other Monthly Payments Used in Qualifying" value={data.allOtherPayments} onChange={v => onChange('allOtherPayments', v)} helperText={isDSCR ? "DSCR: borrower's other obligations are not used to qualify." : undefined} data-id="element-173" />
            </div>
          </div>
        </div>
      </div>

      {/* Ratios */}
      <div className="pt-3 border-t border-slate-100" data-id="element-174">
        <h3 className="text-xs font-bold text-slate-500 mb-2 uppercase tracking-wider" data-id="element-175">
          Ratios
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-x-4 gap-y-2" data-id="element-176">
          <div className="bg-slate-50 p-3 rounded-lg border border-slate-200" data-id="element-177">
            <div className="text-xs text-slate-500 mb-1" data-id="element-178">LTV</div>
            <div className="font-semibold text-slate-900" data-id="element-179">
              {metrics.ltv.toFixed(2)}%
            </div>
          </div>
          <div className="bg-slate-50 p-3 rounded-lg border border-slate-200" data-id="element-180">
            <div className="text-xs text-slate-500 mb-1" data-id="element-181">CLTV / TLTV</div>
            <div className="font-semibold text-slate-900" data-id="element-182">
              {metrics.cltv.toFixed(2)}%
            </div>
          </div>
          <div className="bg-slate-50 p-3 rounded-lg border border-slate-200" data-id="element-183">
            <div className="text-xs text-slate-500 mb-1" data-id="element-184">HCLTV / HTLTV</div>
            <div className="font-semibold text-slate-900" data-id="element-185">
              {metrics.hcltv.toFixed(2)}%
            </div>
          </div>
          <div className="bg-slate-50 p-3 rounded-lg border border-slate-200" data-id="element-186">
            <div className="text-xs text-slate-500 mb-1" data-id="element-187">Housing Ratio</div>
            <div className="font-semibold text-slate-900" data-id="element-188">
              {isDSCR ? <span className="text-slate-400" data-id="element-189">N/A</span> : `${metrics.housingRatio.toFixed(2)}%`}
            </div>
            {isDSCR && <div className="text-[10px] text-slate-400 mt-1" data-id="element-190">
                N/A — DSCR program
              </div>}
          </div>
          <div className="bg-slate-50 p-3 rounded-lg border border-slate-200" data-id="element-191">
            <div className="text-xs text-slate-500 mb-1" data-id="element-192">
              Total Obligations (DTI)
            </div>
            <div className="font-semibold text-slate-900" data-id="element-193">
              {isDSCR ? <span className="text-slate-400" data-id="element-194">N/A</span> : `${metrics.dti.toFixed(2)}%`}
            </div>
            {isDSCR && <div className="text-[10px] text-slate-400 mt-1" data-id="element-195">
                N/A — DSCR program
              </div>}
          </div>
          <div className="bg-slate-50 p-3 rounded-lg border border-slate-200" data-id="element-196">
            <div className="text-xs text-slate-500 mb-1" data-id="element-197">
              DSCR (Rent ÷ PITIA)
            </div>
            <div className="font-semibold text-slate-900" data-id="element-198">
              {isDSCR ? `${metrics.dscr.toFixed(4)}×` : <span className="text-slate-400" data-id="element-199">—</span>}
            </div>
          </div>
        </div>
      </div>

      {/* Qualifying Rate */}
      <div className="pt-3 border-t border-slate-100" data-id="element-200">
        <h3 className="text-xs font-bold text-slate-500 mb-2 uppercase tracking-wider" data-id="element-201">
          Qualifying Rate
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-2" data-id="element-202">
          <Select label="Basis" value={data.qualRateType} onChange={v => onChange('qualRateType', v)} options={[{
          label: 'Rate Used for Qualifying',
          value: 'rateUsed'
        }, {
          label: 'Initial Bought-Down Rate',
          value: 'initialBoughtDown'
        }, {
          label: 'Other',
          value: 'other'
        }]} data-id="element-203" />
          <PercentInput label="Qualifying Rate" value={data.qualRate} onChange={v => onChange('qualRate', v)} required error={errors.qualRate} data-id="element-204" />
          <TextInput label="Appraisal Form Number" value={data.appraisalFormNumber} onChange={v => onChange('appraisalFormNumber', v)} data-id="element-205" />
        </div>
      </div>

      {/* Borrower Funds & Reserves */}
      <div className="pt-3 border-t border-slate-100" data-id="element-206">
        <h3 className="text-xs font-bold text-slate-500 mb-2 uppercase tracking-wider" data-id="element-207">
          Borrower Funds & Reserves
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-2" data-id="element-208">
          <CurrencyInput label="Funds to Close Required" value={data.fundsRequired} onChange={v => onChange('fundsRequired', v)} data-id="element-209" />
          <CurrencyInput label="Verified Assets" value={data.verifiedAssets} onChange={v => onChange('verifiedAssets', v)} data-id="element-210" />
          <SegmentedControl label="Escrow (T&I)" value={data.escrowTI} onChange={v => onChange('escrowTI', v)} options={[{
          label: 'Yes',
          value: true
        }, {
          label: 'No',
          value: false
        }]} data-id="element-211" />
          <NumberInput label="No. of Months Reserves" value={data.monthsReserves} onChange={v => onChange('monthsReserves', v)} data-id="element-212" />
        </div>
      </div>

      {/* Risk Assessment */}
      <div className="pt-3 border-t border-slate-100" data-id="element-213">
        <h3 className="text-xs font-bold text-slate-500 mb-2 uppercase tracking-wider" data-id="element-214">
          Risk Assessment & Property Review
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-2" data-id="element-215">
          <Select label="Underwriting Method" value={data.riskMethod} onChange={v => onChange('riskMethod', v)} options={[{
          label: 'Manual Underwriting',
          value: 'manual'
        }, {
          label: 'AUS',
          value: 'aus'
        }]} data-id="element-216" />
          <PercentInput label="Interested-Party Contributions" value={data.interestedPartyContrib} onChange={v => onChange('interestedPartyContrib', v)} data-id="element-217" />
          <Select label="Level of Property Review" value={data.propertyReviewLevel} onChange={v => onChange('propertyReviewLevel', v)} options={[{
          label: 'Exterior / Interior',
          value: 'exteriorInterior'
        }, {
          label: 'Exterior Only',
          value: 'exteriorOnly'
        }, {
          label: 'No Appraisal',
          value: 'none'
        }]} data-id="element-218" />
          <Select label="AUS Type" value={data.riskMethod === 'manual' ? '' : data.ausType} onChange={v => onChange('ausType', v)} disabled={data.riskMethod === 'manual'} helperText={data.riskMethod === 'manual' ? 'Not applicable — manually underwritten' : undefined} options={[{
          label: 'DU (Fannie)',
          value: 'du'
        }, {
          label: 'LPA (Freddie)',
          value: 'lpa'
        }, {
          label: 'Other',
          value: 'other'
        }]} data-id="element-219" />
          <TextInput label="AUS Recommendation" value={data.ausRecommendation} onChange={v => onChange('ausRecommendation', v)} disabled={data.riskMethod === 'manual'} className="md:col-span-2" data-id="element-220" />
          <SegmentedControl label="Affordable Housing Initiative" value={data.affordableHousing} onChange={v => onChange('affordableHousing', v)} options={[{
          label: 'Yes',
          value: true
        }, {
          label: 'No',
          value: false
        }]} data-id="element-221" />
          <SegmentedControl label="Homeownership Ed. Cert. in File" value={data.homeownershipEd} onChange={v => onChange('homeownershipEd', v)} options={[{
          label: 'Yes',
          value: true
        }, {
          label: 'No',
          value: false
        }]} data-id="element-222" />
        </div>
      </div>

      <div className="pt-3 border-t border-slate-100" data-id="element-223">
        <TextArea label="Underwriter Comments" value={data.underwriterComments} onChange={v => onChange('underwriterComments', v)} required error={errors.underwriterComments} rows={5} data-id="element-224" />
      </div>
    </div>;
}